import { fromPromise, setup } from "xstate";

import { Logger } from "@moment/logging";

import { type AutoUpdateState, type DownloadEvent } from "~/state/types";

const logger = new Logger("desktop.autoupdater.store");

const initialState: AutoUpdateState = {
	isOutdatedAppVersion: false,
	isAppUpdateReady: false,
};

export const autoUpdaterMachine = setup({
	types: {
		context: {
			autoUpdaterState: initialState,
		},
		events: {} as
			| { type: "idle"; output?: AutoUpdateState }
			| { type: "update"; message: DownloadEvent; output?: AutoUpdateState }
			| { type: "reset"; output?: AutoUpdateState }
			| { type: "resetting"; output: AutoUpdateState },
	},
	actors: {
		reset: fromPromise(async () => initialState),
		update: fromPromise(
			async ({ input: { message } }: { input: { message: DownloadEvent } }) => {
				logger.info("got download event");
				switch (message.event) {
					case "started":
						return {
							isOutdatedAppVersion: true,
							isAppUpdateReady: false,
						};
					case "finished":
						return {
							isOutdatedAppVersion: true,
							isAppUpdateReady: true,
						};
					default:
						return;
				}
			}
		),
	},
	actions: {
		setAutoUpdaterState: ({ context, event }) => {
			const data = event?.output;
			if (data === undefined) {
				return;
			}
			context.autoUpdaterState = data;
			return {
				output: data,
			};
		},
	},
}).createMachine({
	id: "auto-updater",
	initial: "idle",
	context: {
		autoUpdaterState: initialState,
	},
	states: {
		idle: {
			on: {
				update: "updating",
				reset: "resetting",
			},
		},
		resetting: {
			invoke: {
				src: "reset",
				onDone: {
					target: "idle",
					actions: "setAutoUpdaterState",
				},
				onError: "error",
			},
		},
		updating: {
			invoke: {
				src: "update",
				input: ({ event }) => {
					if (event?.type !== "update") {
						throw new Error("Invalid event type");
					}
					return { message: event.message };
				},
				onDone: {
					target: "idle",
					actions: "setAutoUpdaterState",
				},
				onError: "error",
			},
		},
		error: {
			type: "final",
		},
	},
});
